import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { VendorsListingService } from '../../services';

interface CustomFeeModalProps {
  open: boolean;
  onClose: () => void;
  vendor: {
    vendorId: string;
    companyName: string;
    customFeePercentage?: number;
  };
  defaultFee: number;
  onSuccess: () => void;
}

export const CustomFeeModal: React.FC<CustomFeeModalProps> = ({
  open,
  onClose,
  vendor,
  defaultFee,
  onSuccess,
}) => {
  const [feePercentage, setFeePercentage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Initialize with current custom fee or empty (to use default)
      const currentFee = vendor.customFeePercentage || 0;
      setFeePercentage(currentFee === 0 ? '' : currentFee.toString());
      setError(null);
    }
  }, [open, vendor]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Parse fee percentage
      const fee = feePercentage.trim() === '' ? 0 : parseFloat(feePercentage);

      // Validation
      if (isNaN(fee)) {
        setError('Please enter a valid number');
        setLoading(false);
        return;
      }

      if (fee < 0 || fee > 100) {
        setError('Fee must be between 0 and 100');
        setLoading(false);
        return;
      }

      // Update vendor fee
      await VendorsListingService.updateVendorFee(vendor.vendorId, fee);

      // Success
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating vendor fee:', err);
      setError(err instanceof Error ? err.message : 'Failed to update fee');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFeePercentage('');
  };

  const currentDisplayFee = feePercentage.trim() === '' || parseFloat(feePercentage) === 0
    ? `${defaultFee}% (default)`
    : `${feePercentage}% (custom)`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Custom Fee - {vendor.companyName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a custom fee percentage for this vendor. Leave empty or set to 0 to use the default platform fee ({defaultFee}%).
          </Typography>

          <TextField
            fullWidth
            label="Custom Fee Percentage"
            type="number"
            value={feePercentage}
            onChange={(e) => setFeePercentage(e.target.value)}
            placeholder={`${defaultFee} (default)`}
            inputProps={{
              min: 0,
              max: 100,
              step: 0.1,
            }}
            helperText="Enter 0 or leave empty to use default fee"
            sx={{ mb: 2 }}
          />

          <Box sx={{ 
            p: 2, 
            bgcolor: 'background.default', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="body2" color="text.secondary">
              Current fee: <strong>{currentDisplayFee}</strong>
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} disabled={loading}>
          Reset to Default
        </Button>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
